import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import React, {useEffect, useMemo, useState, useRef, useCallback} from 'react';
import ServerData from '../../common/serverData';
import {hideLoader, showLoader} from '../../store/common/slice';
import {verifyLoggedIn} from '../../store/auth/actions';
import {calcMonthsTextForSelectBox, shortenText} from '../../common/shared/statelessMethods';
import {StyledDebtList} from './TenantRecordTable.styled';
import { Modal, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import Search from '../../images/search.svg';
import PrintPdfIcons from '../../common/export_documents/PrintPdfIcons';
import Table from '../../common/table/TableFunctional';
import {BllinkLogger} from '../../common/utils/bllink_loggers';
import BllinkUtils from '../../common/utils/utils';
import { constants } from '../../common/constants/constants';
import WarningLetterNew from '../reports/debt_list/warningLetterNew';
import {getBuildingTitle, getBuildingTitleText} from '../../store/buildings/actions';
import {parseApartmentPhone, parseApartmentTenantName} from '../../store/common/tenantInfo';
import {StyledActionsBar} from '../reports/report.styled';
import useBodyClass from '../../common/hooks/useBodyClassName';
import {CurrencyValue} from "../../common/currencyValue";
import { StyledTenantRecordModal, StyledPopover } from "./TenantRecordTable.styled";
import { NotificationManager } from "react-notifications";
import {GAEvent} from "../../common/google_analytics/google_analytics";

export default function TenantRecordTable({match}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const {buildingID} = match.params;
    const MarkTenantRecord = {
        confirm: 'confirm',
        deny: 'deny'
    };

    const accessToken = useSelector((state) => state.auth.accessToken);
    const ltr = useSelector((state) => state.common.ltrEnabled);

    const [listData, setListData] = useState([]);
    const [filters, setFilters] = useState({});
    const [totalDebt, setTotalDebt] = useState(0);
    const [currency, setCurrency] = useState(constants.defaultCurrency);
    const [selectedRow, setSelectedRow] = useState({});
    const [title, setTitle] = useState('');
    const pdfContainer = useRef();
    const [showMarkPaymentModal, setShowMarkPaymentModal] = useState(false);
    const [paymentPlanId, setPaymentPlanId] = useState(null);
    const [selectedAmount, setSelectedAmount] = useState(null);
    const [paymentPayedFor, setPaymentPayedFor] = useState(null);
    const [tableTitle, setTableTitle] = useState('');

    document.title = t('titles.tenant_claims_paid');
    useBodyClass('report');

    let client = new ServerData(null, null, {accessToken: accessToken});

    const columns = useMemo(
        () => [
            {
                Header: t('reports.apartmentName'),
                id: 'ApartmentNumber',
                accessor: 'ApartmentNumber',
            },
            {
                Header: t('reports.tenantName'),
                id: 'ClientName',
                accessor: 'ClientName',
            },
            {
                Header: t('reports.buildingTitle'),
                id: 'buildingTitle',
                accessor: 'buildingTitle',
                minWidth: 200
            },
            {
                Header: t('reports.total_to_pay'),
                id: 'Amount',
                accessor: 'Amount',
                className: 'amount',
                Cell: ({ row }) => {
                    return <>{parseAmount(row.original.Amount)}</>
                }
            },
            {
                Header: t('reports.ownership_type'),
                id: 'TenantType',
                Cell: ({ row }) => {
                    const tenantTypeRaw = row.original.TenantType ? t(`reports.${row.original.TenantType}`) : '';
                    return <>{tenantTypeRaw}</>
                }
            },
            {
                Header: t('reports.payment_for'),
                id: 'paymentFor',
                Cell: ({ row }) => {
                    const text = getMonthsText(row.original);
                    return (
                        <OverlayTrigger
                            placement="top"
                            overlay={props => (
                                <Tooltip {...props}>
                                    {text?.originalComment}
                                </Tooltip>
                            )}
                        >
                            <div exceltext={text?.shortComment || text}>
                                {text?.shortComment || text}
                            </div>
                        </OverlayTrigger>
                    )
                }
            },
            {
                Header: t('reports.payment_comment'),
                id: 'Comment',
                accessor: 'Comment',
            },
            {
                Header: t('payments.payment_method'),
                id: 'Method',
                accessor: 'translatedMethod',
            },
            {
                Header: '',
                accessor: 'markPayment',
                className: 'send-request',
                Cell: ({row}) => {
                    const [showPopover, setShowPopover] = useState(false);
                    return (
                        <OverlayTrigger
                            rootClose
                            show={showPopover}
                            onToggle={() => setShowPopover(!showPopover)}
                            trigger="click"
                            placement="top"
                            overlay={(
                                <StyledPopover>
                                    <Popover.Body>
                                        <div className='link' onClick={() => {
                                            showSolutionPopUp(row.original)
                                            setShowPopover(false)
                                        }}>
                                            {t('reports.mark_payment')}
                                        </div>
                                        <div className='link' onClick={() => denyAndRedirect(row.original.planID)}>
                                            {t('reports.add_meaning_of_payment')}
                                        </div>
                                    </Popover.Body>
                                </StyledPopover>
                            )}>
                            <div className='tool'>...</div>
                        </OverlayTrigger>
                    )
                }
            },
        ],
        [listData]
    );

    const loadTenantRecordsList = async (start, end) => {
        if (!accessToken) {
            try {
                const {payload} = await dispatch(verifyLoggedIn());
                client = new ServerData(null, null, {accessToken: payload.accessToken});
            } catch (e) {
                return;
            }
        }

        await dispatch(getBuildingTitle({buildingID}));
        const titleData = await dispatch(getBuildingTitleText({t, buildingID}));
        setTitle(titleData.payload);

        let response
        response = await client.managers.tenantRecords.getTenantRecordForBuilding(buildingID, start, end) ?? { paymentResults: [] };
        BllinkLogger.info(`response tenant records list ${buildingID} building`, response);

        response.paymentResults = response?.paymentResults?.sort((a, b) => (a.planID > b.planID) ? 1 : ((b.planID > a.planID) ? -1 : 0));
        response.paymentResults?.forEach(item => {
            item.translatedMethod = item.Method ? t(`buildings.${item.Method}`) : '';
            item.tenantPhone = parseApartmentPhone(item.ClientPhone);
            item.tenantName = parseApartmentTenantName(item.tenantDetails);
        });

        return response;
    };

    const parseAmount = amountString => {
        if (amountString) {
            return <span exceltext={amountString}>{BllinkUtils.Strings.numberWithCommas(amountString)}</span>
        }
    };

    const renderCorrectReceipt = (cartIdOrPaymentId, buildingID) => {
        if (!cartIdOrPaymentId) return '';

        const isNumber = Number.isInteger(cartIdOrPaymentId);
        const linkText = t('reports.confirmation');
        let url;
        if (isNumber){
            url = `/managers/reports/confirmation-url/${buildingID}/${cartIdOrPaymentId}`;
            return <a href={url} onClick={e => handleTablesLinksClicked(e, url)}>{linkText}</a>
        } else {
            url = `/${constants.pages.orderStatusPage}/${cartIdOrPaymentId}`;
            return <Link to={url} target='_blank'>{linkText}</Link>;
        }
    };

    const getMonthsText = (paymentPlan) => {
        return paymentPlan.months ? calcMonthsTextForSelectBox(paymentPlan.months) : shortenText(paymentPlan.oneTime?.description);
    }

    const showSolutionPopUp = (paymentPlan) => {
        const text = getMonthsText(paymentPlan);

        setPaymentPlanId(paymentPlan.planID);
        setSelectedAmount(paymentPlan.Amount);
        setPaymentPayedFor(text?.shortComment || text);
        setShowMarkPaymentModal(true);
    }

    const denyAndRedirect = async (paymentPlanId) => {
        await markPayment(MarkTenantRecord.deny, paymentPlanId, true);
    }

    const reloadOrRedirect = (redirect) => {
        if (redirect) {
            dispatch(hideLoader());
            history.push({pathname: `/${constants.pages.tenantDetailsManagerUrl}/${buildingID}`});
        }

        setShowMarkPaymentModal(false);
        setTimeout(() => window.location.reload(), 2000);
    }

    const getSuccessMessageForPopUp = (solution, redirect) => {
        if (redirect){
            return t('reports.transaction.tenant_record_rejected_and_redirect');
        }

        return solution === MarkTenantRecord.confirm
            ? t('reports.transaction.tenant_record_approved')
            : t('reports.transaction.tenant_record_rejected');
    }

    const markPayment = async (solution, planId, redirect = false) => {
        dispatch(showLoader());

        const payload = {solution: solution};
        let result = await client.managers.tenantRecords.postSolutionForTenantRecord(buildingID, planId, payload);

        if (result.success) {
            reportGoogleAnalytics(solution);
            NotificationManager.success(getSuccessMessageForPopUp(solution, redirect));
            reloadOrRedirect(redirect);
        } else {
            NotificationManager.error(t('reports.transaction.tenant_record_transaction_failed'))
        }

        dispatch(hideLoader());
    };

    const reportGoogleAnalytics = (solution) => {
        if (solution === MarkTenantRecord.confirm){
            GAEvent(constants.GoogleAnalytics.Events.offlinePayments, 'tenantRecord', 1, 'tenantRecordApproved');
            GAEvent(constants.GoogleAnalytics.Events.offlinePayments, 'tenantRecord', selectedAmount, 'tenantRecordApprovedAmount');
        }else{
            GAEvent(constants.GoogleAnalytics.Events.offlinePayments, 'tenantRecord', 1, 'tenantRecordDenied');
            GAEvent(constants.GoogleAnalytics.Events.offlinePayments, 'tenantRecord', selectedAmount, 'tenantRecordDeniedAmount');
        }
    }

    const handleTablesLinksClicked = async (e, url) => {
        e.preventDefault();

        dispatch(showLoader());
        const response = await client.get(url);
        dispatch(hideLoader());
        if (response) {
            let win = window.open(response, '_blank');
            win && win.focus();
        }
    }

    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const fetchIdRef = useRef(0);

    const fetchData = useCallback(async ({ pageSize, pageIndex }) => {
        const fetchId = ++fetchIdRef.current;
        if (fetchId === fetchIdRef.current) {
            dispatch(showLoader())
            const startRow = pageSize * pageIndex;
            const endRow = startRow + pageSize;
            const data = await loadTenantRecordsList(startRow, endRow);
            if (data) {
                setPageCount(Math.ceil(data.paymentResults.length / pageSize));
                setCurrentPage(pageIndex);
                setListData(data.paymentResults);
                setTotalDebt(data.Total);
                setCurrency(data.Total.currency)
            }
            dispatch(hideLoader())
        }
    }, []);

    useEffect(() => {
        setTableTitle(
            <OverlayTrigger
                placement="top"
                overlay={props => (<Tooltip {...props}/>)}
            >
                <span className='title_wrapper'>
                    {t('reports.total_tenant_records_title')}
                    <CurrencyValue currency={totalDebt?.currency}>
                        {/* CurrencyValue accepts value in cents, but server sends it as shekel/pesos integer value */}
                        {totalDebt?.value * 100}
                    </CurrencyValue>
                </span>
            </OverlayTrigger>
        );
    }, [currency, totalDebt]);

    const onRowClick = rowData => {
        setSelectedRow(rowData);
    }

    return (
        <StyledDebtList className={buildingID ? 'building-debt' : ''} ltr={ltr}>
            {
                buildingID && (
                    <h1 className='page-title'>
                        <span>{t('reports.building_tenant_records_title')}</span>
                        <span className='building-title'>{title}</span>
                    </h1>
                )
            }
            <StyledActionsBar className={'toolbar align-items-center'}>
                <div className="control-with-icon search actions-toolbar-search">
                    <input
                        type="text"
                        className="form-control bllink-input-border"
                        name="search"
                        onChange={e => setFilters({
                            global: e.target?.value ?? false
                        })}
                    />
                    <img src={Search} alt={t('search.search')} />
                </div>

                <PrintPdfIcons
                    pdfContainer={pdfContainer.current}
                    classes={ltr ? "ml-auto" : "mr-auto"}
                    buildingID={buildingID}
                    reportName={t('reports.building_tenant_records_title')}
                    eventLabel={'managerTenantRecord'}
                    titleString={t('reports.building_tenant_records_list')}
                    excelTitle={t('reports.building_tenant_records_list')}
                />
            </StyledActionsBar>
            <div id="section-to-print" ref={pdfContainer}>
                <Table
                    buildingId={buildingID}
                    columns={columns}
                    data={listData}
                    filters={filters}
                    tableTitle={tableTitle}
                    paginationOptions={{
                        fetchData: fetchData,
                        pageCount: pageCount,
                        currentPage: currentPage
                    }}
                    onRowClick={onRowClick}
                />
            </div>
            {
                selectedRow.apartmentNumber && (
                    <WarningLetterNew
                        row={selectedRow}
                        clearSelectedRow={() => setSelectedRow({})}
                        currency={currency}
                    />
                )
            }

            <StyledTenantRecordModal
                centered
                show={showMarkPaymentModal}
                onHide={() => setShowMarkPaymentModal(false)}
            >
                <Modal.Header>
                    <Modal.Title>{t('reports.mark_payment')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="description-container">
                        <div>{t('reports.payment_for')}:</div>
                        <div>{paymentPayedFor}</div>
                    </div>
                    <div className="buttons-container">
                        <button
                            onClick={() => markPayment(MarkTenantRecord.confirm, paymentPlanId)}
                            type="submit"
                            className="bllink-button short-button js-log-payments-btn"
                        >{t('offline_payments.approveTenantRecord')}</button>
                        <button
                            onClick={() => markPayment(MarkTenantRecord.deny, paymentPlanId)}
                            type="submit"
                            className="btn_12"
                        >{t('offline_payments.denyTenantRecord')}</button>
                    </div>
                </Modal.Body>
            </StyledTenantRecordModal>
        </StyledDebtList>
    );
}
